/**
 * Main configuration loader and manager
 * Integrates demo config, build config, and environment variables
 */

import { getFinalDemoConfig, DemoConfig } from './demo.config';

export interface AppConfig {
  demo: DemoConfig;
  env: {
    nodeEnv: string;
    buildTarget: string;
    apiBaseUrl: string;
    enableMockApi: boolean;
    debugMode: boolean;
  };
  runtime: {
    isDemo: boolean;
    isDevelopment: boolean;
    isProduction: boolean;
    enableHotReload: boolean;
  };
}

class ConfigManager {
  private _config: AppConfig | null = null;

  /**
   * Get the complete application configuration
   */
  get config(): AppConfig {
    if (!this._config) {
      this._config = this.loadConfiguration();
    }
    return this._config;
  }

  /**
   * Reload configuration (useful for testing or dynamic updates)
   */
  reload(): AppConfig {
    this._config = null;
    return this.config;
  }

  /**
   * Load and merge all configuration sources
   */
  private loadConfiguration(): AppConfig {
    const demoConfig = getFinalDemoConfig();
    
    // Extract environment variables
    const nodeEnv = import.meta.env.NODE_ENV || 'development';
    const buildTarget = import.meta.env.BUILD_TARGET || nodeEnv;
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    const enableMockApi = this.parseBoolean(import.meta.env.VITE_ENABLE_MOCK_API, demoConfig.apiMode === 'mock');
    const debugMode = this.parseBoolean(import.meta.env.VITE_DEBUG_MODE, demoConfig.analytics.debugMode);

    // Determine runtime flags
    const isDemo = buildTarget === 'demo' || demoConfig.features.demoControlPanel;
    const isDevelopment = nodeEnv === 'development';
    const isProduction = nodeEnv === 'production';
    const enableHotReload = isDevelopment;

    return {
      demo: demoConfig,
      env: {
        nodeEnv,
        buildTarget,
        apiBaseUrl,
        enableMockApi,
        debugMode,
      },
      runtime: {
        isDemo,
        isDevelopment,
        isProduction,
        enableHotReload,
      },
    };
  }

  /**
   * Parse boolean environment variable
   */
  private parseBoolean(value: string | undefined, defaultValue: boolean): boolean {
    if (value === undefined) return defaultValue;
    return value.toLowerCase() === 'true';
  }

  /**
   * Get feature flag value with environment override
   */
  getFeatureFlag(flag: string): boolean {
    const envKey = `VITE_ENABLE_${flag.toUpperCase().replace(/([A-Z])/g, '_$1')}`;
    const envValue = import.meta.env[envKey];
    
    if (envValue !== undefined) {
      return this.parseBoolean(envValue, false);
    }
    
    // Fall back to demo config
    return (this.config.demo.features as any)[flag] || false;
  }

  /**
   * Get asset configuration with environment overrides
   */
  getAssetConfig(): {
    logoVariant: string;
    theme: string;
    showDemoWatermark: boolean;
    staticAssetPath: string;
  } {
    const config = this.config;
    
    return {
      logoVariant: import.meta.env.VITE_LOGO_VARIANT || config.demo.assets.logoVariant,
      theme: import.meta.env.VITE_THEME || config.demo.assets.theme,
      showDemoWatermark: this.parseBoolean(
        import.meta.env.VITE_SHOW_DEMO_WATERMARK,
        config.demo.assets.showDemoWatermark
      ),
      staticAssetPath: '/demo-assets',
    };
  }

  /**
   * Get mock data configuration with environment overrides
   */
  getMockDataConfig(): {
    businessName: string;
    businessType: string;
    businessIndustry: string;
    monthlyVolume: number;
    defaultAmount: number;
    defaultCurrency: string;
    defaultStablecoin: string;
    simulateFailures: boolean;
    failureRate: number;
    recoveryTime: number;
  } {
    const config = this.config;
    
    return {
      businessName: import.meta.env.VITE_BUSINESS_NAME || config.demo.mockData.businessProfile.name,
      businessType: import.meta.env.VITE_BUSINESS_TYPE || config.demo.mockData.businessProfile.type,
      businessIndustry: import.meta.env.VITE_BUSINESS_INDUSTRY || config.demo.mockData.businessProfile.industry,
      monthlyVolume: parseInt(import.meta.env.VITE_MONTHLY_VOLUME) || config.demo.mockData.businessProfile.monthlyVolume,
      defaultAmount: parseInt(import.meta.env.VITE_DEFAULT_AMOUNT) || config.demo.mockData.transactionDefaults.amount,
      defaultCurrency: import.meta.env.VITE_DEFAULT_CURRENCY || config.demo.mockData.transactionDefaults.currency,
      defaultStablecoin: import.meta.env.VITE_DEFAULT_STABLECOIN || config.demo.mockData.transactionDefaults.stablecoin,
      simulateFailures: this.parseBoolean(
        import.meta.env.VITE_SIMULATE_PROVIDER_FAILURES,
        config.demo.mockData.providerHealth.simulateFailures
      ),
      failureRate: parseFloat(import.meta.env.VITE_PROVIDER_FAILURE_RATE) || config.demo.mockData.providerHealth.failureRate,
      recoveryTime: parseInt(import.meta.env.VITE_PROVIDER_RECOVERY_TIME) || config.demo.mockData.providerHealth.recoveryTime,
    };
  }

  /**
   * Get performance configuration
   */
  getPerformanceConfig(): {
    chunkSizeWarningLimit: number;
    enableLazyLoading: boolean;
    preloadCriticalAssets: boolean;
    enableCodeSplitting: boolean;
    enableCaching: boolean;
    optimizeAssets: boolean;
  } {
    const config = this.config;
    
    return {
      chunkSizeWarningLimit: parseInt(import.meta.env.VITE_CHUNK_SIZE_WARNING_LIMIT) || 500,
      enableLazyLoading: true,
      preloadCriticalAssets: true,
      enableCodeSplitting: this.parseBoolean(import.meta.env.VITE_ENABLE_CODE_SPLITTING, true),
      enableCaching: this.parseBoolean(
        import.meta.env.VITE_ENABLE_CACHING,
        config.demo.performance.enableCaching
      ),
      optimizeAssets: this.parseBoolean(
        import.meta.env.VITE_OPTIMIZE_ASSETS,
        config.demo.performance.optimizeAssets
      ),
    };
  }

  /**
   * Get CDN configuration
   */
  getCDNConfig(): {
    enableCDN: boolean;
    cdnUrl?: string;
    assetPrefix: string;
    publicPath: string;
  } {
    return {
      enableCDN: this.parseBoolean(import.meta.env.VITE_ENABLE_CDN, false),
      cdnUrl: import.meta.env.VITE_CDN_URL,
      assetPrefix: import.meta.env.VITE_ASSET_PREFIX || '',
      publicPath: '/',
    };
  }

  /**
   * Validate configuration
   */
  validate(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const config = this.config;

    // Validate required environment variables
    if (!config.env.apiBaseUrl) {
      errors.push('API base URL is required');
    }

    // Validate feature flag consistency
    if (config.env.enableMockApi && config.demo.apiMode !== 'mock') {
      errors.push('Mock API enabled but demo config API mode is not set to mock');
    }

    // Validate CDN configuration
    const cdnConfig = this.getCDNConfig();
    if (cdnConfig.enableCDN && !cdnConfig.cdnUrl) {
      errors.push('CDN enabled but CDN URL is not configured');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get debug information
   */
  getDebugInfo(): {
    config: AppConfig;
    validation: { valid: boolean; errors: string[] };
    environment: Record<string, any>;
  } {
    return {
      config: this.config,
      validation: this.validate(),
      environment: {
        NODE_ENV: import.meta.env.NODE_ENV,
        BUILD_TARGET: import.meta.env.BUILD_TARGET,
        VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
        VITE_ENABLE_MOCK_API: import.meta.env.VITE_ENABLE_MOCK_API,
        VITE_DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE,
      },
    };
  }
}

// Singleton instance
export const configManager = new ConfigManager();

// Export convenience functions
export const getConfig = () => configManager.config;
export const getFeatureFlag = (flag: string) => configManager.getFeatureFlag(flag);
export const getAssetConfig = () => configManager.getAssetConfig();
export const getMockDataConfig = () => configManager.getMockDataConfig();
export const getPerformanceConfig = () => configManager.getPerformanceConfig();
export const getCDNConfig = () => configManager.getCDNConfig();

// React hook for configuration
export const useConfig = () => {
  const [config] = React.useState(() => configManager.config);
  return config;
};

// React hook for feature flags
export const useFeatureFlag = (flag: string): boolean => {
  const [enabled] = React.useState(() => configManager.getFeatureFlag(flag));
  return enabled;
};

// Add React import
import React from 'react';

// Export types
export type { AppConfig, DemoConfig };